<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import PreviewTable from './PreviewTable.vue'
import TrackRecordService from '@/services/mandate/trackRecordService.ts'
import type {
  TrackRecordAnalyzeResponse,
  TrackRecordMode,
  TrackRecordPerfType,
  TrackRecordPerfUnit,
  TrackRecordWizardMapping,
} from '@/types/services/mandate/trackRecord.type.ts'

const props = defineProps<{
  mandateId: number
  file: File
  mapping: TrackRecordWizardMapping
  perfType: TrackRecordPerfType | null
  perfUnit: TrackRecordPerfUnit | null
  capitalInitial: number
  replaceExisting: boolean
  /** Si déjà analysé (retour depuis étape 3/4), on ne ré-analyse pas */
  initialAnalysis?: TrackRecordAnalyzeResponse
}>()

const emit = defineEmits<{
  (e: 'update:mapping', value: TrackRecordWizardMapping): void
  (e: 'update:perfType', value: TrackRecordPerfType | null): void
  (e: 'update:perfUnit', value: TrackRecordPerfUnit | null): void
  (e: 'update:capitalInitial', value: number): void
  (e: 'update:replaceExisting', value: boolean): void
  (e: 'analyzed', value: TrackRecordAnalyzeResponse): void
  (e: 'next'): void
  (e: 'back'): void
}>()

const { t } = useI18n()

const analysis = ref<TrackRecordAnalyzeResponse | null>(props.initialAnalysis ?? null)
const loading = ref(false)
const analyzeError = ref<string | null>(null)

onMounted(() => {
  // Ne ré-analyse que si aucune analyse n'existe déjà (premier passage)
  if (!analysis.value) {
    runAnalyze()
  }
})

async function runAnalyze() {
  loading.value = true
  analyzeError.value = null
  try {
    const result = await TrackRecordService.analyze(props.mandateId, props.file)
    analysis.value = result
    emit('analyzed', result)

    // Pre-fill mapping from analysis
    const detected = result.detected_mapping
    const mode: TrackRecordMode = detected.value_column ? 'value' : 'perf'
    emit('update:mapping', {
      date_column: detected.date_column,
      mode,
      value_column: detected.value_column,
      perf_column: detected.perf_column,
    })
    if (result.suggested_perf_type) {
      emit('update:perfType', result.suggested_perf_type)
    }
    if (result.detected_perf_unit) {
      emit('update:perfUnit', result.detected_perf_unit)
    }
  } catch {
    analyzeError.value = t('views.trackRecord.errors.generic')
  } finally {
    loading.value = false
  }
}

// Header options excluding already selected columns
const headerOptions = computed(() =>
  (analysis.value?.headers ?? []).map((h) => ({ title: h, value: h }))
)

const dateColumnOptions = computed(() =>
  headerOptions.value.filter((opt) => opt.value !== activeDataColumn.value)
)

const dataColumnOptions = computed(() =>
  headerOptions.value.filter((opt) => opt.value !== props.mapping.date_column)
)

const activeDataColumn = computed(() =>
  props.mapping.mode === 'value' ? props.mapping.value_column : props.mapping.perf_column
)

const modeOptions = [
  { title: t('views.trackRecord.mapping.dataTypeValue'), value: 'value' as TrackRecordMode },
  { title: t('views.trackRecord.mapping.dataTypePerf'), value: 'perf' as TrackRecordMode },
]

const perfTypeOptions = [
  { title: t('views.trackRecord.mapping.perfTypePeriod'), value: 'period' as TrackRecordPerfType },
  { title: t('views.trackRecord.mapping.perfTypeCumulative'), value: 'cumulative' as TrackRecordPerfType },
]

const perfUnitOptions = [
  { title: t('views.trackRecord.mapping.perfUnitAuto'), value: null },
  { title: t('views.trackRecord.mapping.perfUnitDecimal'), value: 'decimal' as TrackRecordPerfUnit },
  { title: t('views.trackRecord.mapping.perfUnitPercent'), value: 'percent' as TrackRecordPerfUnit },
]

function setMode(mode: TrackRecordMode) {
  emit('update:mapping', {
    ...props.mapping,
    mode,
    value_column: mode === 'value' ? props.mapping.value_column : null,
    perf_column: mode === 'perf' ? props.mapping.perf_column : null,
  })
}

function setDateColumn(val: string) {
  emit('update:mapping', { ...props.mapping, date_column: val })
}

function setValueColumn(val: string) {
  emit('update:mapping', { ...props.mapping, value_column: val })
}

function setPerfColumn(val: string) {
  emit('update:mapping', { ...props.mapping, perf_column: val })
}

// Validation
const canContinue = computed(() => {
  if (!props.mapping.date_column || !props.mapping.mode) return false
  if (props.mapping.mode === 'value') return !!props.mapping.value_column
  return !!props.mapping.perf_column && !!props.perfType
})
</script>

<template>
  <div>
    <!-- Loading analyze -->
    <v-row v-if="loading">
      <v-col cols="12" class="d-flex flex-column align-center py-8">
        <v-progress-circular indeterminate color="primary" class="mb-4" />
        <p class="text-body-2 text-medium-emphasis">Analyse du fichier en cours…</p>
      </v-col>
    </v-row>

    <!-- Error -->
    <v-alert
      v-else-if="analyzeError"
      type="error"
      :text="analyzeError"
      class="mb-4"
    />

    <template v-else-if="analysis">
      <!-- Preview -->
      <v-row>
        <v-col cols="12">
          <p class="text-subtitle-2 mb-1">
            {{ t('views.trackRecord.mapping.title') }}
            <v-chip size="x-small" class="ml-2">
              {{ t('views.trackRecord.mapping.rowCount', { count: analysis.row_count }) }}
            </v-chip>
          </p>
          <PreviewTable :headers="analysis.headers" :rows="analysis.preview" />
        </v-col>
      </v-row>

      <!-- Warnings -->
      <v-row v-if="analysis.warnings.length">
        <v-col cols="12">
          <v-alert type="warning" density="compact">
            <p class="font-weight-medium mb-1">{{ t('views.trackRecord.mapping.warnings') }}</p>
            <ul class="pl-4">
              <li v-for="(w, i) in analysis.warnings" :key="i" class="text-body-2">{{ w }}</li>
            </ul>
          </v-alert>
        </v-col>
      </v-row>

      <!-- Mapping form -->
      <v-row class="mt-2">
        <!-- Date column -->
        <v-col cols="12" sm="6">
          <v-select
            :model-value="mapping.date_column"
            :label="t('views.trackRecord.mapping.dateColumn')"
            :items="dateColumnOptions"
            item-title="title"
            item-value="value"
            hide-details="auto"
            @update:model-value="setDateColumn"
          />
        </v-col>

        <!-- Data type -->
        <v-col cols="12" sm="6">
          <v-select
            :model-value="mapping.mode"
            :label="t('views.trackRecord.mapping.dataType')"
            :items="modeOptions"
            item-title="title"
            item-value="value"
            hide-details="auto"
            @update:model-value="setMode"
          />
        </v-col>

        <!-- Value column -->
        <template v-if="mapping.mode === 'value'">
          <v-col cols="12" sm="6">
            <v-select
              :model-value="mapping.value_column"
              :label="t('views.trackRecord.mapping.valueColumn')"
              :items="dataColumnOptions"
              item-title="title"
              item-value="value"
              hide-details="auto"
              @update:model-value="setValueColumn"
            />
          </v-col>
        </template>

        <!-- Perf column + options -->
        <template v-if="mapping.mode === 'perf'">
          <v-col cols="12" sm="6">
            <v-select
              :model-value="mapping.perf_column"
              :label="t('views.trackRecord.mapping.perfColumn')"
              :items="dataColumnOptions"
              item-title="title"
              item-value="value"
              hide-details="auto"
              @update:model-value="setPerfColumn"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              :model-value="perfType"
              :label="t('views.trackRecord.mapping.perfType')"
              :items="perfTypeOptions"
              item-title="title"
              item-value="value"
              hide-details="auto"
              @update:model-value="emit('update:perfType', $event)"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-select
              :model-value="perfUnit"
              :label="t('views.trackRecord.mapping.perfUnit')"
              :items="perfUnitOptions"
              item-title="title"
              item-value="value"
              hide-details="auto"
              @update:model-value="emit('update:perfUnit', $event)"
            />
          </v-col>

          <v-col cols="12" sm="6">
            <v-text-field
              :model-value="capitalInitial"
              :label="t('views.trackRecord.mapping.capitalInitial')"
              type="number"
              density="compact"
              hide-details
              readonly
              disabled
            />
          </v-col>
        </template>

        <!-- Replace existing -->
        <v-col cols="12">
          <v-checkbox
            :model-value="replaceExisting"
            :label="t('views.trackRecord.mapping.replaceExisting')"
            hide-details
            @update:model-value="emit('update:replaceExisting', !!$event)"
          />
          <v-alert
            v-if="replaceExisting"
            type="warning"
            density="compact"
            class="mt-2"
            :text="t('views.trackRecord.mapping.replaceExistingWarning')"
          />
        </v-col>
      </v-row>
    </template>

    <!-- Navigation -->
    <v-row class="mt-2">
      <v-col cols="12" class="d-flex justify-space-between">
        <v-btn variant="text" @click="emit('back')">
          <template #prepend>
            <v-icon icon="PhArrowLeft" />
          </template>
          {{ t('views.trackRecord.buttons.back') }}
        </v-btn>

        <v-btn
          :disabled="!canContinue || loading"
          @click="emit('next')"
        >
          {{ t('views.trackRecord.buttons.next') }}
          <template #append>
            <v-icon icon="PhArrowRight" />
          </template>
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>
